import { createFileRoute, Link } from "@tanstack/react-router"
import { Search, MapPin, AlertTriangle } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { useSites, useAlerts } from "@/lib/hooks/use-sites"
import { fetchSites } from "@/lib/api/sites"
import { fetchAlerts } from "@/lib/api/alerts"
import { trackEvent, Events } from "@/lib/telemetry/analytics"

export const Route = createFileRoute("/_authenticated/")({
  loader: async ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData({
      queryKey: ["sites"],
      queryFn: fetchSites,
    })
    void queryClient.ensureQueryData({
      queryKey: ["alerts", { siteSlug: undefined, active: true }],
      queryFn: () => fetchAlerts({ active: true }),
    })
  },
  component: DashboardPage,
})

function DashboardPage() {
  const { data: sites, isLoading } = useSites()
  const { data: alerts } = useAlerts()
  const [search, setSearch] = useState("")

  const filteredSites = sites?.filter((site) =>
    site.name.toLowerCase().includes(search.toLowerCase()),
  )

  const alertsBySite = alerts?.reduce(
    (acc, alert) => {
      const slug = alert.site
      acc[slug] = (acc[slug] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Sites
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {sites?.length ?? 0} sites available
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sites..."
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-[var(--radius-panel)] bg-[var(--color-bg-secondary)]"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredSites?.map((site) => {
            const siteAlertCount = alertsBySite?.[site.slug] ?? 0
            return (
              <Link
                key={site.slug}
                to="/$siteSlug/workbench"
                params={{ siteSlug: site.slug }}
                onClick={() => trackEvent(Events.SITE_SELECTED, { site: site.slug })}
                className="group rounded-[var(--radius-panel)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] p-4 transition-all duration-[var(--duration-standard)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-bg-tertiary)]"
              >
                <div className="mb-3 flex items-start justify-between">
                  <h3 className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-accent-blue)] transition-colors">
                    {site.name}
                  </h3>
                  {siteAlertCount > 0 && (
                    <Badge variant="destructive" className="ml-2 shrink-0">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      {siteAlertCount}
                    </Badge>
                  )}
                </div>
                <div className="space-y-1.5 text-xs text-[var(--color-text-muted)]">
                  {site.city && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" />
                      <span>
                        {site.city}
                        {site.country ? `, ${site.country}` : ""}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    {site.features?.length > 0 && (
                      <span>{site.features.length} features</span>
                    )}
                    {site.insar_dataset && (
                      <Badge variant="secondary" className="text-[10px]">
                        InSAR
                      </Badge>
                    )}
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
